import { useEffect } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import {
  Checkbox,
  TruncateText,
  typographyVariants,
} from '@mint-lib/tw-components';

import type { DataFilePreview } from '../../api/types.js';
import { initialSearchParams } from '../../constants/tablesConfig.js';
import {
  useSetFooterRowsCount,
  useTableDataFileFooterRows,
  useUploadState,
} from '../../queries/index.js';
import FooterTabEmptyState from '../atoms/FooterTabEmptyState.jsx';
import {
  TableHeadSkeleton,
  TableSkeletonRows,
} from '../molecules/DynamicTableSkeleton.jsx';
import {
  Table,
  TableBody,
  TableBodyCell,
  TableContainer,
  TableRow,
} from '../molecules/TableComponents.jsx';

type FooterTabProps = {
  dataFileId: string;
  dataFilePreview: DataFilePreview;
};

export const FooterTab = ({ dataFileId, dataFilePreview }: FooterTabProps) => {
  const { t } = useTranslation('@myn/import-hub');

  const { data: uploadState } = useUploadState(dataFileId);

  const isUploading = uploadState?.status === 'uploading';

  useEffect(() => {
    if (isUploading) {
      enqueueSnackbar({
        variant: 'warning',
        title: t('Footer loading'),
        subTitle: t(
          'Wait until the file is fully uploaded to choose the footer rows to discard.',
        ),
        removable: true,
      });
    }
  }, [t, isUploading]);

  const { data: tableDataFileFooterRows, isPending } =
    useTableDataFileFooterRows(dataFileId, {
      throwOnError: true,
      enabled: !isUploading,
    });

  const { mutate: setFooterRowsCount } = useSetFooterRowsCount(dataFileId);

  const selectedFooterRowNumber = dataFilePreview.footerRowsCount || 0;
  const totalRows = tableDataFileFooterRows.items.length;

  const handleSelectFooterRow = (rowIndex: number) => (checked: boolean) => {
    const newCount = totalRows - rowIndex;
    setFooterRowsCount({
      footerRowsCount: checked ? newCount : 0,
    });
  };

  const maxCellsCount = tableDataFileFooterRows.items.reduce((acc, row) => {
    return Math.max(acc, row.cells.length);
  }, 0);

  const normalizeCellsCount = (row: { cells: string[]; id: string }) => {
    const missingCellsCount = maxCellsCount - row.cells.length;
    const cells = row.cells.map((value, index) => ({
      key: `${row.id}-cell-${index}`,
      value,
    }));

    if (missingCellsCount > 0) {
      return [
        ...cells,
        ...Array.from({ length: missingCellsCount }, (_, index) => ({
          key: `${row.id}-empty-cell-${row.cells.length + index}`,
          value: '',
        })),
      ];
    }
    return cells;
  };

  const renderFooterRows = () => {
    if (isPending) {
      return <TableSkeletonRows rowsCount={initialSearchParams.itemsPerPage} />;
    }

    if (!tableDataFileFooterRows.items.length) {
      return (
        <TableRow className="pointer-events-none">
          <TableBodyCell>
            <FooterTabEmptyState />
          </TableBodyCell>
        </TableRow>
      );
    }

    return tableDataFileFooterRows.items.map((row, index) => {
      const isSelected = index >= totalRows - selectedFooterRowNumber;
      const isDisabled = index > totalRows - selectedFooterRowNumber;

      return (
        <TableRow
          hover={!isDisabled}
          key={row.id}
          selected={isSelected}
          aria-checked={isSelected}
          className={
            isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
          }
        >
          <TableBodyCell
            align="center"
            bgColor={isSelected ? 'warning' : 'default'}
            className="w-10 p-0"
          >
            <Checkbox
              data-dd-action-name="Footer rows: select row"
              checked={isSelected}
              disabled={isDisabled}
              onCheckedChange={(checked) =>
                handleSelectFooterRow(index)(checked === true)
              }
              aria-label={t('Footer row')}
              className={!isDisabled ? 'cursor-pointer' : undefined}
            />
          </TableBodyCell>
          {normalizeCellsCount(row).map((cell) => (
            <TableBodyCell
              key={cell.key}
              bgColor={isSelected ? 'warning' : 'default'}
              className="w-[12.5rem]"
            >
              <TruncateText>{cell.value}</TruncateText>
            </TableBodyCell>
          ))}
        </TableRow>
      );
    });
  };

  return (
    <TableContainer className="shrink-[1000] bg-ui-primary">
      <div className="flex h-10 shrink-0 items-center px-4">
        <h6 className={typographyVariants({ variant: 'h6' })}>
          {t('Your footer')}
        </h6>
      </div>
      <Table className="flex-1 table-fixed shadow-none">
        {isPending && <TableHeadSkeleton />}
        <TableBody>{renderFooterRows()}</TableBody>
      </Table>
    </TableContainer>
  );
};
